import { useContext, useEffect, useState } from "react";
import {
  AssetRow,
  getUniqueFileName,
  StatusBadge,
} from "../../helpers/creditBonus/helper";
import MiniDropzone from "../../layouts/creditBonus/dropZone";
import { Badge, Button, Label, Select, Tooltip } from "flowbite-react";
import { UploadToStorage } from "../../services/firebaseActions/uploaderActions";
import { RemoveFileExt } from "../../utilities/utilityService";
import { UpdateCampaignAssets } from "../../services/creditBonus/creditBonusActions";
import { ResponseContext } from "../../context/responseProvider";
import EditCampaignForm from "./editCampaignForm";
import { DeleteCbCampaign } from "./deleteCampaign";
import AllowedUsers from "../../routes/allowedUsers";
import { PERMISSIONS } from "../../constants/permissions";

const CampaignDetail = ({ campaign, onClose, handleApi }) => {
  const [local, setLocal] = useState(campaign);
  const [assetFile, setAssetFile] = useState(null);
  const [assetType, setAssetType] = useState("");
  const [assetFileName, setAssetFileName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { getResponse, setResponse } = useContext(ResponseContext);

  useEffect(() => {
    setLocal(campaign);
  }, [campaign]);

  /*   const handleRemoveAsset = async (asset) => {
    setLocal((prev) => ({
      ...prev,
      assets: prev.assets.filter((a) => a.id !== asset.id),
    }));

    await DeleteAssets({
      campaignId: local.id,
      asset,
      email: localStorage.getItem("userEmail"),
      setIsLoading,
      setResponse,
      onSuccess: () => handleApi(true),
    });
  }; */

  const handleSave = async () => {
    try {
      setIsLoading(true);
      if (assetFile) {
        await UploadToStorage(
          assetFileName,
          assetFile,
          "credit_bonus",
          local.id,
          RemoveFileExt(assetFileName),
          setAssetFileName,
          setAssetFile,
          localStorage.getItem("userEmail"),
        ).then(async (url) => {
          await UpdateCampaignAssets({
            campaignId: local.id,
            assetUrl: url || null,
            email: localStorage.getItem("userEmail"),
            assetFileName: assetFileName,
            type: assetType,
            promoType: local.promo_type,
            setIsLoading,
            setResponse,
            onSuccess: () => {
              handleApi();
              setAssetFile(null);
              setAssetFileName("");
              setAssetType("");
            },
          });
        });
      }

      // setTimeout(() => handleApi(false), 500);
    } catch (err) {
      console.error(err);
    }
  };

  const rows = [
    ["Type", local.promo_type],
    [
      "Status",
      <Tooltip content="Status will automatically update">
        <StatusBadge status={local.status} />
      </Tooltip>,
    ],
    ["Period", `${local.start_date} → ${local.end_date}`],
    ["Min. deposit", `AUD ${Number(local.promo_min_deposit).toLocaleString()}`],
    ["Max. credit", `AUD ${Number(local.promo_max_credit).toLocaleString()}`],
    ["Eligible regions", local.allowed_countries],
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white">
            {local.promo_name}
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {local.created_by} ·{" "}
            {local.createdAt.toDate().toLocaleDateString("en-AU", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </p>
        </div>
        {/*  <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg font-bold leading-none"
        >
          ✕
        </button> */}
        <EditCampaignForm data={local} />
      </div>

      {/* Details table */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
          Details
        </p>
        <table className="w-full text-xs">
          <tbody>
            {rows.map(([label, value]) => (
              <tr
                key={label}
                className="border-b border-gray-100 dark:border-gray-700 last:border-0"
              >
                {value !== "" && (
                  <>
                    <td className="py-2 text-gray-400 w-2/5">{label}</td>
                    <td className="py-2 font-medium text-gray-900 dark:text-white">
                      {value}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {local.description && (
          <p className="mt-2 text-xs text-gray-500">{local.promo_comment}</p>
        )}
      </div>

      {/* Assets */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
          Assets
        </p>

        {local?.assets?.length > 0 ? (
          <ul className="mb-2">
            {local.assets?.map((a) => (
              <AssetRow
                key={a.id}
                asset={a}
                isLoading={isLoading}
                // onRemove={(asset) => handleRemoveAsset(asset)}
              />
            ))}
          </ul>
        ) : (
          <p className="text-xs text-gray-400 mb-2">No documents yet.</p>
        )}

        <div className="w-4/12 my-4">
          <Label htmlFor="assetType" value="Asset Type *" />
          <Select
            id="assetType"
            name="assetType"
            value={assetType}
            onChange={(e) => setAssetType(e.target.value)}
          >
            <option value="" disabled>
              Select Asset Type
            </option>
            <option value="header">Banner Image</option>
            <option value="aside">Aside Image</option>
            <option value="terms">Terms & Conditions</option>
          </Select>
        </div>
        <MiniDropzone
          label="Upload document"
          accept=".pdf, .png, .jpg,.jpeg,.svg"
          hint="pdf, png, jpg, svg"
          onFile={(f) => {
            setAssetFile(f);
            setAssetFileName(f ? getUniqueFileName(f.name, local.assets) : "");
          }}
          fileName={assetFileName}
          disabled={isLoading}
        />
      </div>

      <div className="flex flex-col items-start gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
        <div className="flex justify-between w-full">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!assetFile || !assetType || isLoading}
          >
            {isLoading ? "Uploading..." : "Upload"}
          </Button>
          <AllowedUsers permission={[PERMISSIONS.FULL, PERMISSIONS.CB_DELETE]}>
            <DeleteCbCampaign data={local} />
          </AllowedUsers>
        </div>
        <div>
          {getResponse("update_campaign_assets")?.status === true && (
            <Badge color="success">Updated successfully!</Badge>
          )}
          {getResponse("update_campaign_assets")?.status === false && (
            <Badge color="failure">
              {getResponse("update_campaign_assets")?.message}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};
export default CampaignDetail;
